<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'bookings' }"
          >Bookings</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4" v-if="booking">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="col-md-6 col-sm-12 col-xs-12 my-6">
        <v-form ref="form" class="booking-form">
          <v-divider></v-divider>
          <h3 class="py-5 primary--text">Booking Details</h3>
          <br />

          <v-card class="detail-wrapper py-5 px-6">
            <div class="mb-4">
              <label class="text-field-label">Booking Code: </label>
              <span>{{ booking.id }}</span>
            </div>
            <div class="mb-4">
              <label class="text-field-label">Tool Name: </label>
              <router-link
                :to="{
                  name: 'tools.details',
                  params: { id: booking.tool.id }
                }"
                >{{ booking.tool.name }}</router-link
              >
            </div>
            <div class="mb-4">
              <label class="text-field-label">Tool Owner: </label>

              <user-avatar
                :avatar="booking.tool.owner.avatar"
                size="30"
                class="mr-2"
              />
              <router-link
                :to="{
                  name: 'user.details',
                  params: { id: booking.tool.owner.id }
                }"
                >{{ booking.tool.owner.full_name }}</router-link
              >
            </div>
            <div class="mb-4">
              <label class="text-field-label">Renter: </label>
              <user-avatar
                :avatar="booking.renter.avatar"
                size="30"
                class="mr-2"
              />
              <router-link
                :to="{
                  name: 'user.details',
                  params: { id: booking.renter.id }
                }"
                >{{ booking.renter.full_name }}</router-link
              >
            </div>
            <div class="mb-4">
              <label class="text-field-label">Payment Method: </label>
              <span>Stripe</span>
            </div>
            <div class="mb-4">
              <label class="text-field-label">Booked From: </label>
              <span>{{ booking.bookedFrom }}</span>
            </div>
            <div class="mb-4">
              <label class="text-field-label">Booked To: </label>
              <span>{{ booking.bookedTo }}</span>
            </div>
            <div class="mb-4">
              <label class="text-field-label">Paid At: </label>
              <span>{{ booking.paidAt }}</span>
            </div>
            <div class="mb-4">
              <label class="text-field-label">Created At: </label>
              <span>{{ booking.addedDate }}</span>
            </div>
            <div class="mb-4">
              <label class="text-field-label">Status: </label>
              <span>{{ booking.status }}</span>
            </div>
            <div class="mb-4">
              <label class="text-field-label">Calculation: </label>
              <v-divider></v-divider>
              <v-card solo class="detail-wrapper py-5 px-6">
                <div class="mb-4">
                  <label class="text-field-label">Currency: </label>
                  <span>{{ booking.total.currency }}</span>
                </div>
                <div class="mb-4">
                  <label class="text-field-label"
                    >Booking Duration in days:
                  </label>
                  <span>{{ booking.total.booking_duration.in_days }}</span>
                </div>
                <div class="mb-4">
                  <label class="text-field-label">Application Fee: </label>
                  <span>{{ booking.total.application_fee }}</span>
                </div>
                <div class="mb-4">
                  <label class="text-field-label">Service Fee: </label>
                  <span>{{ booking.total.service_fee }}</span>
                </div>
                <div class="mb-4">
                  <label class="text-field-label">Receivable Amount: </label>
                  <span>{{ booking.total.receivable_amount }}</span>
                </div>
                <div class="mb-4">
                  <label class="text-field-label">Sub Total: </label>
                  <span>{{ booking.total.sub_total }}</span>
                </div>
                <div class="mb-4">
                  <strong
                    ><label class="text-field-label">Grand Total: </label>
                    <span>{{ booking.total.grand_total }}</span></strong
                  >
                </div>
              </v-card>
            </div>
          </v-card>
        </v-form>
      </div>
    </div>

    <v-overlay absolute :value="isLoading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import {
  mdiChevronLeft,
  mdiCheck,
  mdiAlertCircleOutline,
  mdiDelete
} from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import Form from '@/utils/form'
import Booking from '@/models/Booking'
import UserAvatar from '@/components/UserAvatar'
export default {
  name: 'BookingDetails',

  components: {
    UserAvatar,
    AppBarNavIcon
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
        delete: mdiDelete
      },

      form: new Form(new Booking()),
      isLoading: false,
      loading: false,
      formMode: this.$route.params.id === 'new' ? 'new' : 'details'
    }
  },

  computed: {
    ...mapState({
      booking: state => state.booking.bookingDetails,
      users: state => state.user.list,
      categories: state => state.category.list
    }),

    displayName() {
      return this.formMode === 'new' ? 'Create Booking' : this.form.name
    },

    disableMessage() {
      const action = this.user && this.user.blocked_at ? 'unblock' : 'block'
      return `Are you sure you want ${action} this account?`
    }
  },

  methods: {
    ...mapActions({
      getBookingDetails: 'booking/getBookingDetails'
    }),

    ...mapMutations({
      clearBookingDetails: 'booking/clearBookingDetails'
    }),

    async getBooking() {
      this.isLoading = true
      this.clearBookingDetails()
      await this.getBookingDetails(this.$route.params.code)
      this.form = new Form(new Booking(this.booking))
      this.isLoading = false
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },

    async initiate() {
      this.isLoading = true

      this.clearBookingDetails()

      if (this.formMode === 'details') {
        this.getBooking()
      } else {
        this.clearBookingDetails()
        this.isLoading = false
      }
    }
  },

  created() {
    this.initiate()
  },

  watch: {
    $route() {
      this.getBooking()
    }
  }
}
</script>
<style scoped>
.detail-wrapper label {
  color: #fc7900;
}
a {
  color: #0065fc;
}
</style>
